import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import {
	MapFiltersFacilities,
	getMapFiltersFacilities,
	updateMapFiltersFacilities as updateDefaultMapFiltersFacilities,
} from "@redux/features/mapFiltersFacilities/mapFiltersFacilitiesSlice"
import {
	getConsumptionMapFiltersFacilities,
	updateConsumptionMapFiltersFacilities,
} from "@redux/features/mapFiltersFacilities/consumptionMapFiltersFacilitiesSlice"
import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { FacilitiesFiltersList } from "@components/map/FacilitiesFiltersList"
import { MapFilterAccordionContent } from "./mapFilterAccordion/MapFilterAccordionContent"
import MapFilterAccordionTriggerMain from "./mapFilterAccordion/MapFilterAccordionTriggerMain"

type MapFiltersFacilitiesSub<O extends keyof MapFiltersFacilities> = MapFiltersFacilities[O]
type SubProp<O extends keyof MapFiltersFacilities> = keyof MapFiltersFacilitiesSub<O>

export type FacilitiesFiltersStorageKey =
	| "mapFiltersFacilities"
	| "consumptionMapFiltersFacilities"

type MapFiltersFacilitiesContentProps = {
	facilitiesStorageKey?: FacilitiesFiltersStorageKey
}

const MapFiltersFacilitiesContent = memo(
	({ facilitiesStorageKey = "mapFiltersFacilities" }: MapFiltersFacilitiesContentProps) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const defaultMapFiltersFacilities = useAppSelector(getMapFiltersFacilities)
	const consumptionMapFiltersFacilities = useAppSelector(
		getConsumptionMapFiltersFacilities
	)
	const mapFiltersFacilities =
		facilitiesStorageKey === "consumptionMapFiltersFacilities"
			? consumptionMapFiltersFacilities
			: defaultMapFiltersFacilities

	const onFilterVisibilityChanged = useCallback(
		<O extends keyof MapFiltersFacilities>(mainProp: O, subProp: SubProp<O>) =>
			(value: boolean) => {
				const nextMapFiltersFacilities = {
					...mapFiltersFacilities,
					[mainProp]: { ...mapFiltersFacilities[mainProp], [subProp]: value },
				}

				if (facilitiesStorageKey === "consumptionMapFiltersFacilities") {
					dispatch(
						updateConsumptionMapFiltersFacilities(nextMapFiltersFacilities)
					)
					return
				}

				dispatch(
					updateDefaultMapFiltersFacilities(nextMapFiltersFacilities)
				)
			},
		[dispatch, facilitiesStorageKey, mapFiltersFacilities]
	)

	return (
		<AccordionPrimitive.Root
			type="single"
			defaultValue="mainItem-1"
			collapsible
		>
			<AccordionPrimitive.Item value="mainItem-1">
				<MapFilterAccordionTriggerMain>{t("FACILITIES")}</MapFilterAccordionTriggerMain>
				<MapFilterAccordionContent>
					<FacilitiesFiltersList
						filters={mapFiltersFacilities.facilities}
						onFilterVisibilityChanged={onFilterVisibilityChanged}
					/>
				</MapFilterAccordionContent>
			</AccordionPrimitive.Item>
		</AccordionPrimitive.Root>
	)
})

export default MapFiltersFacilitiesContent
